import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  createContact,
  getContact,
  listContacts,
  patchContactProfileFacts,
  serverErrorMessage,
  setContactArchived,
  setContactFavorite,
  setContactMuted,
} from '../lib/api.js';
import type { ContactDetailDto, ContactListItemDto } from '../types.js';
import { ContactDetailPanel, type ContactDetailTab } from './AddressBook.js';
import { ChannelBadge } from './ChannelBadge.js';
import { ProfilePhoto } from './ProfilePhoto.js';
import { EmptyState } from './states/EmptyState.js';
import { ErrorState } from './states/ErrorState.js';
import { LoadingState } from './states/LoadingState.js';
import { Dialog } from './ui/Dialog.js';

/**
 * batch-189 — the Contacts tab, redesigned as an ADDRESS BOOK (mockup
 * docs/mockups/contacts-v2.html). A two-pane master/detail: a name-sorted A–Z
 * list (pinned ★ Favorites, sort toggle, search, + New, A–Z jump rail) on the
 * left; the selected contact's detail (hero + tabbed tools, reusing
 * ContactDetailPanel) on the right. Light/dark via the unified theme tokens;
 * responsive (single-pane → full-page detail on narrow/iPad-portrait). The whole
 * book is loaded once and grouped/searched/sorted client-side. No new send path.
 */

type SortKey = 'name' | 'company' | 'recent';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'A–Z' },
  { key: 'company', label: 'Company' },
  { key: 'recent', label: 'Recent' },
];

// batch-191 — channel filter. `null` = all. Matches the inbox CHANNEL_OPTIONS order.
const CHANNEL_FILTERS: { value: string | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
];

interface Props {
  selectedContactId: string | null;
  onSelectContactId: (id: string | null) => void;
  onOpenThread: (id: string) => void;
  // R22 — the Identity-matches pinned row (absorbs the Admin view) + the count, and
  // the "Work in LinkedIn engagement" deep link. Both navigate to a routed view.
  onShowIdentityMatches: () => void;
  identityMatchesCount: number | undefined;
  onOpenEngagement: () => void;
}

function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || '?';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?';
}

/** Section bucket letter: first A–Z letter, else '#'. */
function bucket(value: string | null | undefined): string {
  const ch = (value ?? '').trim().charAt(0).toUpperCase();
  return ch >= 'A' && ch <= 'Z' ? ch : '#';
}

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

function sortContacts(list: ContactListItemDto[], sort: SortKey): ContactListItemDto[] {
  const byName = (a: ContactListItemDto, b: ContactListItemDto) =>
    collator.compare(a.display_name ?? '~', b.display_name ?? '~') || a.id.localeCompare(b.id);
  if (sort === 'name') return [...list].sort(byName);
  if (sort === 'company') {
    return [...list].sort(
      (a, b) => collator.compare(a.company ?? '~~', b.company ?? '~~') || byName(a, b),
    );
  }
  // recent — newest interaction first, nulls last.
  return [...list].sort((a, b) => {
    const av = a.last_interaction_at ? Date.parse(a.last_interaction_at) : -1;
    const bv = b.last_interaction_at ? Date.parse(b.last_interaction_at) : -1;
    return bv - av || byName(a, b);
  });
}

interface Section {
  key: string;
  label: string;
  items: ContactListItemDto[];
}

/** Group the (already sorted) list into address-book sections. */
function buildSections(list: ContactListItemDto[], sort: SortKey): Section[] {
  const sorted = sortContacts(list, sort);
  if (sort === 'recent') {
    return sorted.length ? [{ key: 'recent', label: 'Recently contacted', items: sorted }] : [];
  }
  const keyOf = (c: ContactListItemDto) => bucket(sort === 'company' ? c.company : c.display_name);
  const map = new Map<string, ContactListItemDto[]>();
  for (const c of sorted) {
    const k = keyOf(c);
    const arr = map.get(k) ?? [];
    arr.push(c);
    map.set(k, arr);
  }
  // A–Z first, '#' last.
  return [...map.entries()]
    .sort(([a], [b]) => (a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)))
    .map(([k, items]) => ({
      key: k,
      label: k === '#' ? (sort === 'company' ? 'No company' : '#') : k,
      items,
    }));
}

export function UnifiedAddressBook({
  selectedContactId,
  onSelectContactId,
  onOpenThread,
  onShowIdentityMatches,
  identityMatchesCount,
  onOpenEngagement,
}: Props) {
  const [sort, setSort] = useState<SortKey>('name');
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const rollRef = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: ['contacts', 'address-book', showArchived ? 'all' : 'active'],
    queryFn: ({ signal }) =>
      listContacts({ limit: 5000, archived: showArchived ? 'all' : 'active', signal }).then(
        (r) => r.items,
      ),
    refetchInterval: 60_000,
  });

  const all = useMemo(() => query.data ?? [], [query.data]);

  // batch-189 (review) — if the selected contact falls out of the loaded archive
  // scope (e.g. "Show archived" toggled OFF while viewing an archived contact),
  // deselect so the detail pane doesn't keep showing an orphaned contact with no
  // matching row. Guarded on a settled, non-empty fetch so a deep-linked
  // selection isn't cleared mid-load; search-narrowing (which filters `all` →
  // `filtered`) intentionally does NOT deselect.
  useEffect(() => {
    if (!selectedContactId || query.isPending || all.length === 0) return;
    if (!all.some((c) => c.id === selectedContactId)) onSelectContactId(null);
  }, [all, query.isPending, selectedContactId, onSelectContactId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) =>
      [c.display_name, c.company, c.role, ...(c.channels ?? [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [all, search]);

  // batch-191 — channel filter (client-side, like search). `null` = all channels.
  const visible = useMemo(
    () => (channel ? filtered.filter((c) => (c.channels ?? []).includes(channel)) : filtered),
    [filtered, channel],
  );

  const favorites = useMemo(
    () =>
      sortContacts(
        visible.filter((c) => c.is_favorite),
        sort,
      ),
    [visible, sort],
  );
  const sections = useMemo(() => buildSections(visible, sort), [visible, sort]);
  const showRail = sort !== 'recent';

  function jumpTo(key: string) {
    rollRef.current
      ?.querySelector(`[data-section="${key === '★' ? 'fav' : key}"]`)
      ?.scrollIntoView({ block: 'start' });
  }

  return (
    <div className={`ab-page${selectedContactId ? ' ab-show-detail' : ''}`}>
      {/* LEFT — the A–Z list */}
      <section className="ab-list" aria-label="Contacts">
        <div className="ab-list-head">
          <div className="ab-title-row">
            <span className="ab-count">{all.length.toLocaleString()} contacts</span>
            <button type="button" className="ab-new-btn" onClick={() => setShowNew(true)}>
              + New
            </button>
          </div>
          <label className="ab-search">
            <span aria-hidden>🔍</span>
            <input
              type="search"
              placeholder="Search name, company, channel…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="ab-toolbar">
            <div className="ab-seg" role="tablist" aria-label="Sort contacts">
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  role="tab"
                  aria-selected={sort === s.key}
                  className={sort === s.key ? 'on' : ''}
                  onClick={() => setSort(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <label className="ab-archived-toggle">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
          </div>
          <div className="ab-channels" role="tablist" aria-label="Filter by channel">
            {CHANNEL_FILTERS.map((ch) => (
              <button
                key={ch.value ?? 'all'}
                type="button"
                role="tab"
                aria-selected={channel === ch.value}
                className={`ab-chan${channel === ch.value ? ' on' : ''}`}
                onClick={() => setChannel(ch.value)}
              >
                {ch.label}
              </button>
            ))}
          </div>
        </div>

        {/* R22 — pinned: Identity matches (absorbs the old Admin/rail item) + a deep
            link into the LinkedIn engagement queue. */}
        <div className="ab-pinned">
          <button
            type="button"
            className="ab-pinned-row"
            data-testid="ab-identity-matches"
            onClick={onShowIdentityMatches}
          >
            <span>🔗 Contact hygiene</span>
            {identityMatchesCount ? (
              <span className="ab-pinned-count">{identityMatchesCount}</span>
            ) : null}
            <span className="ab-pinned-arrow" aria-hidden>
              ›
            </span>
          </button>
          <button
            type="button"
            className="ab-pinned-row"
            data-testid="ab-open-engagement"
            onClick={onOpenEngagement}
          >
            <span>💬 Work in LinkedIn engagement</span>
            <span className="ab-pinned-arrow" aria-hidden>
              →
            </span>
          </button>
        </div>

        {query.isPending ? (
          <LoadingState label="Loading contacts" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : all.length === 0 ? (
          <EmptyState title="No contacts" hint="Add one with “+ New”." />
        ) : filtered.length === 0 ? (
          <EmptyState title="No matches" hint="Try a different search." />
        ) : (
          <div className="ab-roll" ref={rollRef}>
            {favorites.length > 0 ? (
              <div data-section="fav">
                <div className="ab-section ab-section-fav">
                  <span>★ FAVORITES</span>
                  <span>{favorites.length}</span>
                </div>
                {favorites.map((c) => (
                  <ContactRow
                    key={`fav-${c.id}`}
                    contact={c}
                    selected={c.id === selectedContactId}
                    onSelect={() => onSelectContactId(c.id)}
                  />
                ))}
              </div>
            ) : null}
            {sections.map((sec) => (
              <div key={sec.key} data-section={sec.key}>
                <div className="ab-section">
                  <span>{sec.label}</span>
                  <span>{sec.items.length}</span>
                </div>
                {sec.items.map((c) => (
                  <ContactRow
                    key={c.id}
                    contact={c}
                    selected={c.id === selectedContactId}
                    onSelect={() => onSelectContactId(c.id)}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {showRail && sections.length > 0 ? (
          <nav className="ab-az" aria-label="Jump to letter">
            {favorites.length > 0 ? (
              <button type="button" className="ab-az-fav" onClick={() => jumpTo('★')}>
                ★
              </button>
            ) : null}
            {sections.map((sec) => (
              <button type="button" key={sec.key} onClick={() => jumpTo(sec.key)}>
                {sec.label.length === 1 ? sec.label : sec.key === '#' ? '#' : sec.label.charAt(0)}
              </button>
            ))}
          </nav>
        ) : null}
      </section>

      {/* RIGHT — the detail */}
      <section className="ab-detail" aria-label="Contact detail">
        {selectedContactId ? (
          <AddressBookDetail
            // R22-23 review — key by contact so a switch remounts with fresh local
            // state (confirmDelete/menuOpen/editing/tab don't leak across contacts).
            key={selectedContactId}
            contactId={selectedContactId}
            onBack={() => onSelectContactId(null)}
            onOpenThread={onOpenThread}
            onArchived={() => onSelectContactId(null)}
          />
        ) : (
          <EmptyState
            title="Select a contact"
            hint="Pick a name from the list to see their full profile, channels, and history."
          />
        )}
      </section>

      {showNew ? (
        <NewContactModal onClose={() => setShowNew(false)} onCreated={onSelectContactId} />
      ) : null}
    </div>
  );
}

function ContactRow({
  contact,
  selected,
  onSelect,
}: {
  contact: ContactListItemDto;
  selected: boolean;
  onSelect: () => void;
}) {
  const sub = [contact.role, contact.company].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      className={`ab-row${selected ? ' sel' : ''}`}
      onClick={onSelect}
      data-testid="ab-row"
    >
      <span className="ab-ava">{initials(contact.display_name)}</span>
      <span className="ab-row-main">
        <span className="ab-row-name">
          {contact.display_name ?? 'Unknown contact'}
          {contact.is_favorite ? <span className="ab-fstar">★</span> : null}
          {contact.archived_at ? <span className="ab-archived-badge">archived</span> : null}
        </span>
        {sub ? <span className="ab-row-sub">{sub}</span> : null}
      </span>
      <span className="ab-dots">
        {(contact.channels ?? []).slice(0, 4).map((ch) => (
          <ChannelBadge key={ch} channel={ch} size="xs" />
        ))}
      </span>
    </button>
  );
}

const TABS: { key: ContactDetailTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'auto', label: 'Auto-respond' },
  { key: 'activity', label: 'Activity' },
  { key: 'identities', label: 'Identities' },
];

function AddressBookDetail({
  contactId,
  onBack,
  onOpenThread,
  onArchived,
}: {
  contactId: string;
  onBack: () => void;
  onOpenThread: (id: string) => void;
  onArchived: () => void;
}) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ContactDetailTab>('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  // R22 — the reversible-delete confirmation moved off window.confirm onto the Dialog.
  const [confirmDelete, setConfirmDelete] = useState(false);
  // batch-343 (F8) — surface a failed favorite/mute/delete/restore instead of a
  // silent no-op (the delete Dialog closes before the mutation resolves, so a
  // failure otherwise looks like the contact was deleted when it wasn't).
  const [actionErr, setActionErr] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['contact', contactId],
    queryFn: ({ signal }) => getContact(contactId, { signal }),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    void queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
  }

  const favorite = useMutation({
    mutationFn: (next: boolean) => setContactFavorite(contactId, next),
    onSuccess: () => {
      setActionErr(null);
      invalidate();
    },
    onError: (e) => setActionErr(serverErrorMessage(e, 'favorite failed')),
  });
  const mute = useMutation({
    mutationFn: (next: boolean) => setContactMuted(contactId, next),
    onSuccess: () => {
      setActionErr(null);
      invalidate();
    },
    onError: (e) => setActionErr(serverErrorMessage(e, 'mute failed')),
  });
  const archive = useMutation({
    mutationFn: (next: boolean) => setContactArchived(contactId, next),
    onSuccess: (_d, archived) => {
      setActionErr(null);
      invalidate();
      setMenuOpen(false);
      if (archived) onArchived();
    },
    onError: (e, next) =>
      setActionErr(serverErrorMessage(e, next ? 'delete failed' : 'restore failed')),
  });

  if (query.isPending) return <LoadingState label="Loading contact" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const c: ContactDetailDto = query.data;
  const facts = c.profile_facts ?? null;
  const channels = Array.from(new Set(c.identities.map((i) => i.channel)));
  const latestThread = c.threads[0]?.id ?? null;
  const isArchived = Boolean(c.archived_at);

  return (
    <div className="ab-detail-inner">
      <button type="button" className="ab-back" onClick={onBack}>
        ‹ Contacts
      </button>

      <div className="ab-hero">
        <div className="ab-hero-top">
          {/* batch-201 — render via ProfilePhoto (same as the Inbox): a same-origin
              cached avatar when present, else an initials chip — never a broken-image
              glyph from a hotlink-blocked media.licdn.com URL. */}
          <ProfilePhoto
            src={facts?.profile_image_url ?? null}
            label={c.display_name ?? 'Contact'}
            className="ab-hero-ava"
          />
          <div className="ab-hero-id">
            <h1>
              {c.display_name ?? 'Unknown contact'}
              <button
                type="button"
                className={`ab-star${c.is_favorite ? ' on' : ''}`}
                aria-label={c.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                aria-pressed={c.is_favorite ?? false}
                disabled={favorite.isPending}
                onClick={() => favorite.mutate(!c.is_favorite)}
              >
                {c.is_favorite ? '★' : '☆'}
              </button>
              {isArchived ? <span className="ab-archived-badge">archived</span> : null}
            </h1>
            {facts && (facts.role || facts.company) ? (
              <div className="ab-hero-role">
                {[facts.role, facts.company].filter(Boolean).join(' · ')}
              </div>
            ) : null}
            {facts?.location ? <div className="ab-hero-loc">📍 {facts.location}</div> : null}
          </div>
        </div>

        <div className="ab-chips">
          {channels.map((ch) => (
            <span className="ab-chip" key={ch}>
              <ChannelBadge channel={ch} size="xs" />
            </span>
          ))}
          <span className="ab-chip ab-chip-muted">
            {c.identities.length} {c.identities.length === 1 ? 'identity' : 'identities'} ·{' '}
            {c.threads.length} {c.threads.length === 1 ? 'conversation' : 'conversations'}
          </span>
        </div>

        <div className="ab-actions">
          <button
            type="button"
            className="ab-btn primary"
            disabled={!latestThread}
            onClick={() => latestThread && onOpenThread(latestThread)}
          >
            Message
          </button>
          <button type="button" className="ab-btn" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button type="button" className="ab-btn" onClick={() => setTab('identities')}>
            Merge…
          </button>
          <button
            type="button"
            className="ab-btn"
            disabled={mute.isPending}
            onClick={() => mute.mutate(!c.muted)}
          >
            {c.muted ? 'Unmute' : 'Mute'}
          </button>
          <div className="ab-more-wrap">
            <button
              type="button"
              className="ab-btn ab-more"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              ⋯
            </button>
            {menuOpen ? (
              <div className="ab-menu" role="menu">
                {isArchived ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="ab-menu-item"
                    disabled={archive.isPending}
                    onClick={() => archive.mutate(false)}
                  >
                    ♻ Restore contact
                  </button>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    className="ab-menu-item danger"
                    disabled={archive.isPending}
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmDelete(true);
                    }}
                  >
                    🗑 Delete contact…
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* R22 — reversible-delete confirmation (replaces window.confirm). */}
        <Dialog
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          label="Delete contact"
          testId="ab-delete-dialog"
          className="ab-confirm"
        >
          <h2 className="ab-confirm-title">Delete {c.display_name ?? 'this contact'}?</h2>
          <p className="ab-confirm-body">
            This <strong>archives</strong> the contact and removes it from your address book. Linked
            conversations and history are kept, and you can restore it any time with “Show
            archived”. Nothing is permanently deleted.
          </p>
          <div className="ab-confirm-actions">
            <button type="button" className="ab-btn" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="ab-btn ab-confirm-danger"
              data-testid="ab-delete-confirm"
              disabled={archive.isPending}
              onClick={() => {
                setConfirmDelete(false);
                archive.mutate(true);
              }}
            >
              Delete contact
            </button>
          </div>
        </Dialog>

        {actionErr ? (
          <p className="ab-action-error" data-testid="ab-action-error" role="alert">
            {actionErr}
          </p>
        ) : null}

        <div className="ab-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`ab-tab${tab === t.key ? ' on' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ab-tabbody">
        <ContactDetailPanel
          contactId={contactId}
          tab={tab}
          hideHeader
          onSelectThread={onOpenThread}
        />
      </div>

      {editing ? (
        <EditFactsModal
          contactId={contactId}
          company={facts?.company ?? null}
          role={facts?.role ?? null}
          location={facts?.location ?? null}
          onClose={() => setEditing(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
            void queryClient.invalidateQueries({ queryKey: ['contacts'] });
            setEditing(false);
          }}
        />
      ) : null}
    </div>
  );
}

function EditFactsModal({
  contactId,
  company,
  role,
  location,
  onClose,
  onSaved,
}: {
  contactId: string;
  company: string | null;
  role: string | null;
  location: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [c, setC] = useState(company ?? '');
  const [r, setR] = useState(role ?? '');
  const [l, setL] = useState(location ?? '');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      patchContactProfileFacts(contactId, {
        company: c.trim() || null,
        role: r.trim() || null,
        location: l.trim() || null,
      }),
    onMutate: () => setErr(null),
    onSuccess: onSaved,
    onError: (e) => setErr(serverErrorMessage(e, 'save failed')),
  });
  return (
    <div className="ab-modal">
      <button type="button" className="ab-modal-backdrop" aria-label="Close" onClick={onClose} />
      <div className="ab-modal-card" role="dialog" aria-modal="true" aria-label="Edit details">
        <h3>Edit details</h3>
        <label className="ab-field">
          Role
          <input value={r} onChange={(e) => setR(e.target.value)} maxLength={200} />
        </label>
        <label className="ab-field">
          Company
          <input value={c} onChange={(e) => setC(e.target.value)} maxLength={200} />
        </label>
        <label className="ab-field">
          Location
          <input value={l} onChange={(e) => setL(e.target.value)} maxLength={200} />
        </label>
        {err ? (
          <p className="ab-action-error" role="alert">
            {err}
          </p>
        ) : null}
        <div className="ab-modal-acts">
          <button type="button" className="ab-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ab-btn primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function NewContactModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [location, setLocation] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      createContact({
        display_name: name.trim(),
        company: company.trim() || null,
        role: role.trim() || null,
        location: location.trim() || null,
      }),
    onMutate: () => setErr(null),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      onCreated(res.contact_id);
      onClose();
    },
    onError: (e) => setErr(serverErrorMessage(e, 'create failed')),
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim()) create.mutate();
  }
  return (
    <div className="ab-modal">
      <button type="button" className="ab-modal-backdrop" aria-label="Close" onClick={onClose} />
      <form className="ab-modal-card" onSubmit={submit} aria-label="New contact">
        <h3>New contact</h3>
        <label className="ab-field">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder="Full name"
            // biome-ignore lint/a11y/noAutofocus: focus the first field on open
            autoFocus
          />
        </label>
        <label className="ab-field">
          Role
          <input value={role} onChange={(e) => setRole(e.target.value)} maxLength={200} />
        </label>
        <label className="ab-field">
          Company
          <input value={company} onChange={(e) => setCompany(e.target.value)} maxLength={200} />
        </label>
        <label className="ab-field">
          Location
          <input value={location} onChange={(e) => setLocation(e.target.value)} maxLength={200} />
        </label>
        {err ? (
          <p className="ab-action-error" data-testid="ab-create-error" role="alert">
            {err}
          </p>
        ) : null}
        <div className="ab-modal-acts">
          <button type="button" className="ab-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="ab-btn primary"
            disabled={!name.trim() || create.isPending}
          >
            Create
          </button>
        </div>
      </form>
    </div>
  );
}
